import {
  Bold,
  Brackets,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Paperclip,
  Redo2,
  SquareCode,
  Strikethrough,
  Table as TableIcon,
  TextQuote,
  Undo2,
} from "lucide-react";
import type { Command } from "@codemirror/view";
import { yUndoManagerKeymap } from "y-codemirror.next";
import { getEditor } from "@/editor/registry";
import { toggleWrap, insertLink, setHeading, toggleLinePrefix, insertBlock } from "@/editor/commands";
import { handleFiles } from "@/editor/attachments";
import { IconButton } from "@/ui/IconButton";
import s from "./EditorToolbar.module.css";

// y-codemirror.next only exposes undo/redo through its keymap
const yUndo = (yUndoManagerKeymap.find((b) => b.key === "Mod-z")?.run ?? (() => false)) as Command;
const yRedo = (yUndoManagerKeymap.find((b) => b.key === "Mod-y" || b.key === "Mod-Shift-z")?.run ??
  (() => false)) as Command;

/**
 * The always-there formatting bar, docked under the editor. Complements
 * the floating selection toolbar and the slash menu: same commands, always
 * visible, zero markdown required.
 */
export function EditorToolbar({ noteId }: { noteId: string }) {
  const run = (cmd: Command) => {
    const view = getEditor(noteId);
    if (!view) return;
    cmd(view);
    view.focus();
  };

  const attach = () => {
    const view = getEditor(noteId);
    if (!view) return;
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = () => {
      if (input.files?.length) handleFiles(view, input.files, view.state.selection.main.head);
    };
    input.click();
  };

  const btn = (label: string, icon: React.ReactNode, cmd: Command, kbd?: string) => (
    <IconButton
      label={label}
      kbd={kbd}
      tooltipSide="top"
      onMouseDown={(e) => {
        e.preventDefault();
        run(cmd);
      }}
    >
      {icon}
    </IconButton>
  );

  return (
    <div className={s.bar} role="toolbar" aria-label="Formatting">
      <span className={s.group}>
        {btn("Undo", <Undo2 size={15} />, yUndo, "Mod+Z")}
        {btn("Redo", <Redo2 size={15} />, yRedo, "Mod+Shift+Z")}
      </span>
      <span className={s.sep} />
      <span className={s.group}>
        {btn("Heading 1", <Heading1 size={15} />, setHeading(1))}
        {btn("Heading 2", <Heading2 size={15} />, setHeading(2))}
        {btn("Heading 3", <Heading3 size={15} />, setHeading(3))}
      </span>
      <span className={s.sep} />
      <span className={s.group}>
        {btn("Bold", <Bold size={15} />, toggleWrap("**"), "Mod+B")}
        {btn("Italic", <Italic size={15} />, toggleWrap("*"), "Mod+I")}
        {btn("Strikethrough", <Strikethrough size={15} />, toggleWrap("~~"), "Mod+Shift+X")}
        {btn("Highlight", <Highlighter size={15} />, toggleWrap("=="), "Mod+Shift+H")}
        {btn("Inline code", <Code size={15} />, toggleWrap("`"), "Mod+E")}
      </span>
      <span className={s.sep} />
      <span className={s.group}>
        {btn("Link", <LinkIcon size={15} />, insertLink, "Mod+K")}
        {btn("Wikilink", <Brackets size={15} />, insertBlockInline("[[$]]"))}
      </span>
      <span className={s.sep} />
      <span className={s.group}>
        {btn("Bullet list", <List size={15} />, toggleLinePrefix("bullet"))}
        {btn("Numbered list", <ListOrdered size={15} />, toggleLinePrefix("numbered"))}
        {btn("Task list", <ListTodo size={15} />, toggleLinePrefix("task"))}
        {btn("Quote", <TextQuote size={15} />, toggleLinePrefix("quote"))}
      </span>
      <span className={s.sep} />
      <span className={s.group}>
        {btn("Code block", <SquareCode size={15} />, insertBlock("```\n$\n```"))}
        {btn("Table", <TableIcon size={15} />, insertBlock("| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n| $ |  |  |\n"))}
        {btn("Divider", <Minus size={15} />, insertBlock("---\n$"))}
        <IconButton label="Attach file" tooltipSide="top" onMouseDown={(e) => { e.preventDefault(); attach(); }}>
          <Paperclip size={15} />
        </IconButton>
      </span>
    </div>
  );
}

/** Inline snippet at the cursor (not on its own line), $ = cursor. */
function insertBlockInline(snippet: string): Command {
  return (view) => {
    const head = view.state.selection.main.head;
    const cursorIdx = snippet.indexOf("$");
    const body = snippet.replace("$", "");
    view.dispatch({
      changes: { from: head, insert: body },
      selection: { anchor: head + (cursorIdx === -1 ? body.length : cursorIdx) },
      userEvent: "input",
    });
    return true;
  };
}
